import React, { useDeferredValue, useEffect, useMemo, useState } from "react"
import moment from "moment"
import { MdLockReset } from "react-icons/md"
import Table, { TableColumn } from "component/table/table"
import MainTabsTableHeader from "component/mainTabsTableHeader/mainTabsTableHeader"
import { TablePaginationBar, useClientListPagination } from "component/tablePagination"
import CustomizedMenu from "component/muiMenu/muiMenu"
import IconsColor from "util/iconsColor"
import utilStyles from "../../asset/styles/util/util.module.scss"
import { applyFilters, applySearch, cycleSort, sortRowsBy } from "util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "context/listFilterContext"
import { useUserDetail } from "context/userDetailContext"
import { useSnackbar } from "context/snackbarContext"
import { agent } from "apiClient/api"
import { copyText as copyToClipboard } from "component/configuration/configurationClipboard"
import {
    ConfigurationUser,
    UserListRow,
    USER_FILTER_ACCESSORS,
    USER_FILTER_GROUPS,
    isSelfConfigurationUser,
    mapUserListItemToConfigurationUser,
    toUserListRow,
    userSearchFields,
} from "component/configuration/configurationUsers"
import CreateUserPopup from "component/configuration/createUserPopup"
import EditUserPopup from "component/configuration/editUserPopup"
import ResetPasswordPopup from "component/configuration/resetPasswordPopup"
import PasswordUpdatedPopup from "component/configuration/passwordUpdatedPopup"
import SetUserStatusPopup from "component/configuration/setUserStatusPopup"

const UserManagement: React.FC = () => {
    const { userDetail } = useUserDetail()
    const { showSuccess, showError } = useSnackbar()
    const {
        search: searchValue, setSearch: setSearchValue,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.configurationUsers)

    const [users, setUsers] = useState<ConfigurationUser[]>([])
    const [createOpen, setCreateOpen] = useState(false)
    const [editUser, setEditUser] = useState<ConfigurationUser | null>(null)
    const [resetUser, setResetUser] = useState<ConfigurationUser | null>(null)
    const [successUser, setSuccessUser] = useState<ConfigurationUser | null>(null)
    const [successPassword, setSuccessPassword] = useState("")
    const [statusUser, setStatusUser] = useState<ConfigurationUser | null>(null)

    const fetchUsers = async () => {
        try {
            const response = await agent.UserListAPI.get()
            if (response?.status === true) {
                setUsers((response?.data ?? []).map(mapUserListItemToConfigurationUser))
            } else {
                showError(response?.error?.message || "Failed to load users")
            }
        } catch (err) {
            console.error("Get user list error", err)
            showError("Failed to load users")
        }
    }

    useEffect(() => {
        fetchUsers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loggedInId = userDetail?.private_user_id

    const copyText = async (text: string, message: string) => {
        const copied = await copyToClipboard(text)
        if (copied) showSuccess(message)
        else showError("Failed to copy")
    }

    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )

    const deferredSearch = useDeferredValue(searchValue)
    const deferredFilters = useDeferredValue(tableSelectedFilters)
    const filteredUsers = useMemo(() => {
        let rows = applyFilters(users, deferredFilters, USER_FILTER_ACCESSORS)
        rows = applySearch(rows, deferredSearch, userSearchFields)
        return sortRowsBy(rows.map(toUserListRow), sortState)
    }, [users, deferredFilters, deferredSearch, sortState])

    const paginationQueryKey = JSON.stringify({ search: searchValue, filters: tableSelectedFilters, sortState })
    const {
        pagedRows,
        total: pagedTotal,
        page: currentPage,
        pageSize: currentPageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useClientListPagination(filteredUsers, {
        page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
    })

    const columns: TableColumn<UserListRow>[] = [
        { key: "user_display", label: "User", width: "16%", align: "left" },
        { key: "username", label: "User name", width: "16%", align: "left" },
        { key: "role_name", label: "Role", width: "10%", align: "left" },
        {
            key: "flag_active",
            label: "Status",
            width: "10%",
            align: "left",
            render: (value: boolean) => (
                <span className={value ? utilStyles.statusBadgeActive : utilStyles.statusBadgeInactive}>
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "last_login_at",
            label: "Last login",
            width: "16%",
            align: "left",
            render: (value: string | null) => (value ? moment(value).format("DD-MMM-YYYY hh:mm A") : "—"),
        },
        { key: "updated_by_user_name", label: "Editor", width: "14%", align: "left" },
        {
            key: "actions",
            label: "Actions",
            width: "8%",
            align: "left",
            render: (_: unknown, row: UserListRow) => {
                const isSelf = isSelfConfigurationUser(row, loggedInId)
                const menuItems = [
                    {
                        label: "Edit User",
                        icon: <IconsColor iconName="editIcon" width="16" height="16" fillColor="none" strokeColor="currentColor" />,
                        onClick: () => setEditUser(row),
                    },
                    {
                        label: "Reset Password",
                        icon: <MdLockReset size={16} />,
                        onClick: () => setResetUser(row),
                    },
                ]
                if (!isSelf) {
                    menuItems.push({
                        label: `Set as ${row.flag_active ? "Inactive" : "Active"}`,
                        icon: <IconsColor iconName="warningSign" width="16" height="16" fillColor="none" strokeColor="currentColor" />,
                        onClick: () => setStatusUser(row),
                    })
                }
                return (
                    <CustomizedMenu
                        buttonText=""
                        menuItems={menuItems}
                        buttonVariant="text"
                        buttonStyle={{ padding: "0px", minWidth: "0px" }}
                        buttonEndIcon={
                            <IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />
                        }
                    />
                )
            },
        },
    ]

    return (
        <>
            <div className={`${utilStyles.pageContainer} ${utilStyles.listPageFill}`}>
                <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search users"
                        totalCount={users.length}
                        filteredCount={filteredUsers.length}
                        itemLabel="users"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={USER_FILTER_GROUPS}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
                        addButtonLabel="Add new user"
                        onAddClick={() => setCreateOpen(true)}
                        showResultCount={false}
                    />
                </div>
                <div className={utilStyles.listPageTableBlock}>
                    <div className={utilStyles.listPageTableScroll}>
                        <Table
                            columns={columns}
                            data={pagedRows}
                            keyExtractor={(row) => row.private_user_id}
                            emptyMessage="No users available"
                            minWidth={1100}
                            fillHeight={true}
                            sortKey={sortState.key}
                            sortDirection={sortState.direction}
                            onSort={handleColumnSort}
                        />
                    </div>
                    <TablePaginationBar
                        total={pagedTotal}
                        page={currentPage}
                        pageSize={currentPageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </div>
            </div>
            <CreateUserPopup
                isOpen={createOpen}
                onClose={() => setCreateOpen(false)}
                onSuccess={fetchUsers}
            />
            <EditUserPopup
                isOpen={Boolean(editUser)}
                user={editUser}
                isSelf={Boolean(editUser && isSelfConfigurationUser(editUser, loggedInId))}
                onClose={() => setEditUser(null)}
                onSuccess={fetchUsers}
            />
            <ResetPasswordPopup
                isOpen={Boolean(resetUser) && !successUser}
                user={resetUser}
                onClose={() => setResetUser(null)}
                onCopy={copyText}
                onUpdated={(newPassword) => {
                    setSuccessUser(resetUser)
                    setSuccessPassword(newPassword)
                    setResetUser(null)
                }}
            />
            <PasswordUpdatedPopup
                isOpen={Boolean(successUser)}
                user={successUser}
                newPassword={successPassword}
                onCopy={copyText}
                onClose={() => {
                    setSuccessUser(null)
                    setSuccessPassword("")
                }}
            />
            <SetUserStatusPopup
                isOpen={Boolean(statusUser)}
                user={statusUser}
                onCancel={() => setStatusUser(null)}
                onSuccess={fetchUsers}
            />
        </>
    )
}

export default UserManagement
